import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
    GetOption,
    HateoasResourceOperation,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { Environment, ENVIRONMENT, NetworkController, NetworkSyncV2, NetworkV2 } from '@netfoundry-ui/shared/model';
import { FeatureService, HTTP_CLIENT, LoggerService, RefresherService } from '@netfoundry-ui/shared/services';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { set } from 'lodash';

export const NETWORK_SERVICE = new InjectionToken<HttpClient>('NETWORK_SERVICE');

@Injectable({ providedIn: 'root' })
export class NetworkServiceV2 extends HateoasResourceOperation<NetworkV2> {
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 2000,
        }
    };
    lastPageCount = 0;
    lastTotalCount = 0;
    _networks = new BehaviorSubject<NetworkV2[]>([]);
    networkRefresher: any;
    public networks: any = [];
    networkLimitSource = new ReplaySubject<any>();
    networkLimit = this.networkLimitSource.asObservable();
    theNetworkLimitIs: any = {};
    apiUrl = '';

    constructor(
        private logger: LoggerService,
        private auth: AuthorizationService,
        private featureService: FeatureService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment,
        private refresher: RefresherService
    ) {
        super(NetworkV2);
        this.apiUrl = environment.v3Enabled ? environment.v3apiUrl : environment.v2apiUrl;
        this.initService();
    }

    override getResource(): Observable<NetworkV2> {
        throw new Error('Do not use: see get getNetwork');
    }

    override getPage(): Observable<PagedResourceCollection<NetworkV2>> {
        throw new Error('Do not use: see getNetworks');
    }

    getNetwork(id: string, options: GetOption = {}): Promise<NetworkV2 | undefined> {
        // embed=extensions lets callers (route guards, side-nav filter, etc.) gate
        // extension-specific surfaces from the same fetch that loads the current network.
        //
        // Gated on v3Enabled because `extensions` exists only on the v3 API. This library is
        // shared: both apps consume NetworkServiceV2 (the `apiv2` name refers to the V2 *models*,
        // not the API version), and nfconsole points it at v2 — so asking for the embed
        // unconditionally sends v2 something it doesn't know. v3Enabled is the flag every sibling
        // service here already switches on (edge-router, cluster, ipsec-tunneler, …).
        //
        // Embeds MUST be merged rather than assigned: callers pass their own `embed`
        // (ziti-dashboard and side-button-bar send `clusters`, ziti-controller.service sends
        // `clusters,network-controllers`) and a plain spread would silently drop it — the same
        // last-value-wins hazard described on the getNetworks() embed below.
        const callerParams: any = options.params ?? {};
        const embeds = new Set<string>(
            typeof callerParams.embed === 'string'
                ? callerParams.embed
                      .split(',')
                      .map((s: string) => s.trim())
                      .filter(Boolean)
                : []
        );
        if (this.environment.v3Enabled) {
            embeds.add('extensions');
        }
        const params = { ...callerParams, ...(embeds.size ? { embed: Array.from(embeds).join(',') } : {}) };
        return super
            .getResource(id, { ...NetworkServiceV2.defaultHttpAccept, ...options, params })
            .toPromise()
            .then((network) => {
                if (network) {
                    // Do NOT read this off `_embedded`. This path goes through the hateoas client,
                    // and ResourceUtils.resolvePayloadProperties hoists every `_embedded` key onto
                    // the resource and then deletes `_embedded` itself — so `extensions` is already
                    // sitting here, and `_embedded?.extensions ?? []` would overwrite the real list
                    // with an empty one. getNetworks() below still reads `_embedded` because it
                    // issues a raw HttpClient GET, where the envelope survives.
                    //
                    // Still defaulted, so `extensions` is always a resolved array even when the
                    // embed was never asked for (nfconsole/v2). api.service.setCurrentNetwork
                    // re-fetches whenever it isn't one, which would otherwise fire on every
                    // nfconsole network switch.
                    const resource = network as any;
                    resource.extensions = resource.extensions ?? [];
                }
                return network;
            });
    }

    public downloadNetwork(id: string, format = 'text/csv', embedAll = false, embed = ''): Observable<any> {
        const headers = new HttpHeaders().set('Accept', format);
        const params = new HttpParams();
        const embedStr = embedAll ? '?embed=all' : '?embed=' + embed;
        return this.http.get(this.apiUrl + 'networks/' + id + embedStr, {
            headers: headers,
            params: params,
            responseType: 'text',
        });
    }

    public downloadNetworkAssembly(id: string, format = 'text/csv', embedAll = false, entities = 'all'): Observable<any> {
      const headers = new HttpHeaders().set('Accept', format);
      const params = new HttpParams();
      const entitiesParam = `?entities=${entities}`;
      return this.http.get(this.apiUrl + 'network-assemblies/' + id + entitiesParam, {
        headers: headers,
        params: params,
        responseType: 'text',
      });
    }

    public downloadNetworkJwt(id: string) {
      const headers = new HttpHeaders();
      const params = new HttpParams();
      return this.http.get(this.apiUrl + 'network-jwt-sets/' + id, {
        headers: headers,
        params: params,
      }).toPromise();
    }

    public getsdsConfigPortEnabled(id: string): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.get(this.apiUrl + 'network-controllers/' + id + '?embed=host,softwareDeploymentState', {
            headers: headers,
        });
    }

    public getNetworkVersions(): Observable<any>  {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.get(this.apiUrl + 'network-versions/', {
            headers: headers,
        });
    }

    public toggleConfigPort(id: string, enableSoftwareConfigPort: boolean): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.patch(
            this.apiUrl + 'software-deployment-states/' + id,
            { softwareConfigurationPortEnabled: enableSoftwareConfigPort },
            {
                headers: headers,
            }
        );
    }

    async getNetworksPage(options?: PagedGetOption): Promise<NetworkV2[]> {
        if (options || this._networks.getValue().length === 0) {
            return super
                .getPage({ ...NetworkServiceV2.defaultHttpAccept, ...options })
                .pipe(
                    tap((val) => {
                        this.lastPageCount = val.totalPages;
                        this.lastTotalCount = val.totalElements;
                    })
                )
                .toPromise()
                .then((network) => network?.resources || ([] as NetworkV2[]));
        }
        return Promise.resolve(this._networks.getValue());
    }

    async getNetworks(options?: PagedGetOption): Promise<NetworkV2[]> {
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const url = `${this.apiUrl}networks`;
        // `extensions` joins the embed list on every cloud-ziti networks-list call. The
        // current network is set from this response (app.component picks the entry whose id
        // matches the route's networkId and forwards it to apiService.setCurrentNetwork)
        // so without the embed here, the side-navigator's extension-gated nav items see
        // an undefined extensions array and stay hidden. Server cost is one batched
        // listForNetworks() SELECT per page — negligible.
        //
        // Embed values MUST be merged into a single comma-joined param rather than appended
        // to the URL alongside a separate `params: { embed: ... }`. Angular's HttpClient
        // would then send two `embed=` query parameters and Spring's @RequestParam String
        // binding picks only the last one, silently dropping our defaults — including
        // `extensions`, which the route guard depends on.
        //
        // Two different axes here, deliberately: `network-controllers` is a cloud-ziti concern,
        // while `extensions` is a v3-API concern keyed off the same v3Enabled flag `this.apiUrl`
        // above resolves from. nfconsole leaves v3Enabled unset, so this.apiUrl is v2apiUrl and
        // requesting `extensions` would send v2 an embed it doesn't know.
        const defaultEmbed = this.featureService.isCloudZiti ? ['network-controllers', 'clusters'] : ['clusters'];
        if (this.environment.v3Enabled) {
            defaultEmbed.push('extensions');
        }
        const pageParams = options?.pageParams || {};
        const callerParams: any = options?.params || {};
        const callerEmbed: string | undefined = callerParams.embed;
        const mergedEmbed = Array.from(
            new Set([...defaultEmbed, ...(callerEmbed ? callerEmbed.split(',').map((s) => s.trim()) : [])])
        ).join(',');
        const finalParams = { ...pageParams, ...callerParams, embed: mergedEmbed };
        return this.http
            .get(url, {
                headers: headers,
                params: finalParams,
            })
            .toPromise()
            .then((result: any) => {
                this.lastPageCount = result.totalPages;
                this.lastTotalCount = result.totalElements;
                const networks: any[] = (result?._embedded?.networkList as NetworkV2[]) || [];
                // `_embedded.extensions` → `network.extensions`, per entry. This call is a raw
                // HttpClient GET, so the HAL envelope is exactly as the server sent it and the
                // embed has to be lifted by hand — unlike getNetwork() above, where the hateoas
                // client has already hoisted it. Lets the side-navigator and route guard read
                // `network.extensions` whether the current network came from this list (the
                // typical app.component switch path) or from a direct getNetwork(id) call.
                //
                // Unconditional, matching getNetwork(): on nfconsole nothing is embedded, but
                // stamping `[]` keeps api.service.setCurrentNetwork from re-fetching against
                // v3apiUrl on every network switch just to resolve the field.
                networks.forEach((n) => {
                    n.extensions = n?._embedded?.extensions ?? [];
                });
                return networks;
            });
    }

    restoreNetwork(networkId: string): Observable<any> {
        const options: any = {};
        const url = `${this.apiUrl}networks/${networkId}/repair`;
        return this.http.post(url, options) as Observable<any>;

    }

    findByNetworkGroupId(networkGroupId: string): Observable<PagedResourceCollection<NetworkV2>> {
        return super.getPage({ ...NetworkServiceV2.defaultHttpAccept, params: { networkGroupId } });
    }

    syncNetworks(network: NetworkV2, networkSyncList: NetworkSyncV2[]) {
        this.http.post(network['_links']['networks'].href + '/sync', networkSyncList).subscribe((resp) => {
            this.logger.info('Network sync response ', resp);
        });
    }

    createNetwork(networkConfig: any, format = 'application/json'): Observable<any> {
        const headers = new HttpHeaders().set('ContentType', format);
        return this.http.post(this.apiUrl + 'networks', networkConfig, {
            headers: headers,
            params: {},
            responseType: 'text',
        });
    }

    public createNetworkAssembly(networkConfig: any, format = 'application/json'): Observable<any> {
      const headers = new HttpHeaders().set('ContentType', format);
      return this.http.post(this.apiUrl + 'network-assemblies/', networkConfig, {
        headers: headers,
        params: {},
        responseType: 'text',
      });
    }

    updateNetwork(networkConfig: any, format = 'application/json'): Observable<any> {
        const headers = new HttpHeaders().set('ContentType', format);
        return this.http.put(this.apiUrl + 'networks', networkConfig, {
            headers: headers,
            params: {},
            responseType: 'text',
        });
    }

    createResources(networkId: string, resourceData: any, format = 'application/json') {
        const headers = new HttpHeaders().set('ContentType', format);
        return this.http.put(this.apiUrl + 'networks/' + networkId, resourceData, {
            headers: headers,
            params: {},
            responseType: 'text',
        });
    }

    public renameNetwork(id: string, newName: string): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.patch(
            this.apiUrl + 'networks/' + id,
            { name: newName },
            {
                headers: headers,
            }
        );
    }

    public addPublicCertificate(id: string, enablePublicCertificate: boolean): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.patch(
            this.apiUrl + 'networks/' + id,
            { publicCertEnabled: enablePublicCertificate },
            {
                headers: headers,
            }
        );
    }

    public enableHA(id: string, enableHA: boolean): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.patch(
            this.apiUrl + 'networks/' + id,
            { haEnabled: enableHA },
            {
                headers: headers,
            }
        );
    }

    public enableFips(id: string, enableFips: boolean): Observable<any> {
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.patch(
            this.apiUrl + 'networks/' + id,
            { fipsEnabled: enableFips },
            {
                headers: headers,
            }
        );
    }

    public getNetworkControllerSecrets(id: string): Observable<any> {
        return this.http.get(this.apiUrl + 'network-controllers/' + id + '/secrets');
    }

    public getNetworkControllerSession(id: string): Observable<any> {
        return this.http.get(this.apiUrl + 'network-controllers/' + id + '/session');
    }

    public getZitiSessionToken(id: string): Observable<any> {
        const body = {
            type: 'session',
        };
        return this.http.post(this.apiUrl + 'networks/' + id + '/exchange', body);
    }

    public getZitiSession(id: string): Observable<any> {
        const body = {
            type: 'auto',
        };
        return this.http.post(this.apiUrl + 'networks/' + id + '/exchange', body);
    }

    public getZitiBearerToken(id: string): Observable<any> {
        const body = {
            type: 'bearer',
        };
        return this.http.post(this.apiUrl + 'networks/' + id + '/exchange', body);
    }

    public getNetworkControllerInfo(id: string): Observable<any> {
        return this.http.get(this.apiUrl + 'network-controllers/' + id + '?embed=all');
    }

    public restart(networkId: string): Observable<string> {
        const options: PagedGetOption = {};
        const url = `${this.apiUrl}networks/${networkId}/network-controller.host/restart`;
        return this.http.post(url, options) as Observable<any>;
    }

    public restartProcess(networkId: string): Observable<string> {
        const options: PagedGetOption = {};
        const url = `${this.apiUrl}networks/${networkId}/network-controller.process/restart`;
        return this.http.post(url, options) as Observable<any>;
    }

    public resize(networkId: string, size: string): Observable<string> {
        const body: any = { size };
        const url = `${this.apiUrl}networks/${networkId}/network-controller.host/resize`;
        return this.http.post(url, body) as Observable<any>;
    }

    public move(id: string, provider: string, region: string, ipAddress: string): Observable<string> {
        const options: any = {};
        const body: any = { provider, region, ipAddress };
        const url = `${this.apiUrl}network-controllers/${id}/move`;
        return this.http.post(url, body, options) as Observable<any>;
    }

    public createBackup(id: string): Observable<string> {
        const options: any = {};
        const url = `${this.apiUrl}network-controllers/${id}/backups`;
        return this.http.post(url, options) as Observable<any>;
    }

    public validateUpdate(networkId: string, model: any) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .patch(this.apiUrl + 'networks/' + networkId, model, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    public async validateCreate(model: any) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .post(this.apiUrl + 'networks', model, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    public listBackups(id: string): Observable<any> {
        const options: any = {};
        const url = `${this.apiUrl}network-controllers/${id}/backups`;
        return this.http.get(url, options) as Observable<any>;
    }

    public restoreBackup(id: string, backupFile: string): Observable<string> {
        const options: any = {};
        const body: any = { backupFile };
        const url = `${this.apiUrl}network-controllers/${id}/restore`;
        return this.http.post(url, body, options) as Observable<any>;
    }
    public restoreBackupNetwork(id: string, backupFile: string): Observable<string> {
        const options: any = {};
        const body: any = { backupFile };
        const url = `${this.apiUrl}networks/${id}/restore`;
        return this.http.post(url, body, options) as Observable<any>;
    }
    public createNetworkBackup(id: string): Observable<string> {
        const options: any = {};
        const url = `${this.apiUrl}networks/${id}/backups`;
        return this.http.post(url, options) as Observable<any>;
    }
    /* accept is opt in so existing callers keep the application/json response they
     * already parse. The Operations Backups page asks for HAL, which returns the
     * collection under _embedded. */
    public listBackupsNetwork(id: string, accept?: string): Observable<any> {
        const options: any = {};
        if (accept) {
            options.headers = new HttpHeaders().set('Accept', accept);
        }
        const url = `${this.apiUrl}networks/${id}/backups`;
        return this.http.get(url, options) as Observable<any>;
    }

    public downloadToCsv(): Observable<any> {
        const params = new HttpParams().set('size', this.lastTotalCount.toString());
        const headers = new HttpHeaders().set('Accept', 'text/csv');
        return this.http.get(this.apiUrl + 'networks', {
            headers: headers,
            params: params,
            responseType: 'text',
        });
    }

    public downloadFileFormat(networkId: string, format = 'text/csv'): Observable<any> {
        const params = new HttpParams().set('networkId', networkId).set('size', this.lastTotalCount.toString());
        const headers = new HttpHeaders().set('Accept', format);
        return this.http.get(this.apiUrl + 'networks', {
            headers: headers,
            params: params,
            responseType: 'text',
        });
    }

    public getNetworkLimits(networkId: string) {
        const params = new HttpParams().set('networkId', networkId).set('page', 0).set('size', 2000);
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        return this.http.get(this.apiUrl + 'network-limits', {
            headers: headers,
            params: params,
        });
    }

    public suspend(networkId: string): Observable<string> {
        const options: PagedGetOption = {};
        const url = `${this.apiUrl}networks/${networkId}/suspend`;
        return this.http.post(url, options) as Observable<any>;
    }

    public resume(networkId: string): Observable<string> {
        const options: PagedGetOption = {};
        const url = `${this.apiUrl}networks/${networkId}/resume`;
        return this.http.post(url, options) as Observable<any>;
    }

    /**
     * Switch a per-network feature extension on. The body is keyed the same way the
     * `embed=extensions` read returns it, so enable/disable/read all speak of the
     * extension by the same field name.
     *
     * The identifiers live in NETWORK_EXTENSIONS (shared/services) — pass one of those
     * rather than a literal, so a write can never name something the route guards and
     * the side-nav filter cannot match.
     */
    public enableExtension(networkId: string, extension: string): Observable<any> {
        const url = `${this.apiUrl}networks/${networkId}/extensions/${extension}`;
        return this.http.put(url, {}) as Observable<any>;
    }

    /**
     * Switch a per-network feature extension off. DELETE carries a body here, which
     * HttpClient only sends when it is passed inside the options object — `delete(url,
     * body)` would quietly send the body as request *options* and no body at all.
     */
    public disableExtension(networkId: string, extension: string): Observable<any> {
        const url = `${this.apiUrl}networks/${networkId}/extensions/${extension}`;
        return this.http.delete(url) as Observable<any>;
    }

    public getEmbeddedHostInfo(ids: string[]): Observable<any> {
        const params = new HttpParams().set('id', ids.join(',')).set('embed', 'host');
        return this.http.get(this.apiUrl + 'network-controllers', {
            params: params,
            responseType: 'json',
        });
    }

    refreshNetworks() {
        if (this.networkRefresher) clearTimeout(this.networkRefresher);
        if (this.auth.canListNetworks()) {
            let params = {};
            if (this.featureService.isCloudZiti) {
              params = {
                embed: 'network-controllers,clusters'
              };
            } else {
              params = {
                embed: 'clusters'
              };
            }
            super
                .getPage({
                    headers: {
                        accept: 'application/hal+json',
                    },
                    pageParams: {
                        page: 0,
                        size: 2000,
                    },
                    params
                })
                .pipe(
                    tap((val) => {
                        this.lastPageCount = val.totalPages;
                        this.lastTotalCount = val.totalElements;
                    })
                )
                .subscribe((networks) => {
                    this._networks.next(networks.resources);
                    this.networks = networks.resources;
                });
        }

        this.networkRefresher = setTimeout(() => {
            this.refreshNetworks();
        }, this.refresher.refreshInterval);
    }

    setCurrentNetworkLimit(networkLimit: any) {
        this.networkLimitSource.next(networkLimit);
        this.theNetworkLimitIs = networkLimit;
    }

    public getSingleEmbeddedHost(id: string): Observable<any>  {
        const params = new HttpParams().set('embed', 'host');
        return this.http.get(this.apiUrl + 'network-controllers?id=' + id, {
            params: params,
            responseType: 'json',
        });
    }

    private getNetworkCSV(id: string, format = 'text/csv', embedAll = false, embed = ''): Observable<any> {
        const headers = new HttpHeaders().set('Accept', format);
        const params = new HttpParams();
        const embedStr = embedAll ? '?embed=all' : '?embed=' + embed;
        return this.http.get(this.apiUrl + 'networks/' + id + embedStr, {
            headers: headers,
            params: params,
            responseType: 'text',
        });
    }

    private async initService() {
        await this.refreshNetworks();
    }
}
